/* ----------------------------------------------------------------------------
   AUTO GIFTS — per-product attachment (metafield-driven)
   ----------------------------------------------------------------------------
   Each main product in the store can have a metafield `custom.attached_gifts`
   (type: List of products) that defines which gifts should be auto-added
   when that product is in the cart.

   This script:
     1. Reads `#auto-gifts-config[data-rules]` from the cart drawer (Liquid
        renders the rules for products currently in cart on every render).
        Reads it FRESH on every cart:updated — because the theme replaces
        the cart drawer body via `innerHTML =`, which does NOT re-execute
        <script> tags. A `data-*` attribute is the only reliable carrier.
     2. On cart:updated, computes the union of all gift variant_ids needed
        by all non-gift products in cart, deduplicated.
     3. POSTs add/remove deltas to /cart/add.js and /cart/update.js, tagging
        every added gift line with `_is_gift: 'true'` and
        `_gift_for: <parent_product_id>` so the rendering layer can nest
        each gift under the product that triggered it.
     4. Dispatches `cart:refresh` to make the theme re-fetch and re-render
        the section. The new render carries fresh rules.

   Loop protection:
     - `syncing` flag prevents re-entry inside the ~700ms after a sync.
     - Sync that doesn't change anything is a silent no-op (no refresh
       dispatch), so the second render after a real change settles cleanly.

   Discounting (free) is handled separately in the Shopify backend.
   -------------------------------------------------------------------------- */
(function () {
  if (window.__arkticAutoGiftsLoaded) return;
  window.__arkticAutoGiftsLoaded = true;

  let syncing = false;
  let rerunRequested = false;

  // ARKTIC: public readiness signal so the checkout button can WAIT for any
  // in-flight gift add/reorder before navigating. `pending` is true from the
  // moment a sync starts doing real work (add/remove/reorder) until the cart
  // has settled. `whenSettled()` resolves immediately when nothing is pending,
  // otherwise on the next settle. This closes the race where a fast click on
  // "Checkout" leaves before the gift's /cart/add.js POST has resolved.
  const waiters = [];
  const AutoGifts = {
    pending: false,
    _settle() {
      this.pending = false;
      while (waiters.length) {
        try { waiters.shift()(); } catch (e) {}
      }
    },
    whenSettled() {
      if (!this.pending) return Promise.resolve();
      return new Promise((resolve) => waiters.push(resolve));
    },
  };
  window.ArkticAutoGifts = AutoGifts;

  function readRules() {
    const el = document.getElementById('auto-gifts-config');
    if (!el) return {};
    try {
      // data-rules is a JSON map { "<product_id>": [<gift_variant_id>, ...] }
      return JSON.parse(el.dataset.rules || '{}');
    } catch (err) {
      console.error('[auto-gifts] bad data-rules:', err);
      return {};
    }
  }

  function isGift(item) {
    return item && item.properties && item.properties._is_gift === 'true';
  }

  function computeNeededGifts(cart, rules) {
    // For each non-gift product in cart, union its rule entries.
    // Map variant_id → parent_product_id (first-cart-order parent wins,
    // since that's the natural nesting anchor).
    const needed = new Map(); // variantId → parentProductId
    for (const item of cart.items) {
      if (isGift(item)) continue;
      const pid = String(item.product_id);
      const giftVids = rules[pid];
      if (!Array.isArray(giftVids)) continue;
      for (const vid of giftVids) {
        const vidNum = Number(vid);
        if (!vidNum) continue;
        if (!needed.has(vidNum)) needed.set(vidNum, pid);
      }
    }
    return needed;
  }

  function findGiftLines(cart) {
    return cart.items.filter(isGift);
  }

  async function fetchCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!res.ok) throw new Error('cart fetch failed');
    return res.json();
  }

  async function addGifts(neededMap, toAddVids) {
    const items = toAddVids.map((vid) => ({
      id: vid,
      quantity: 1,
      properties: {
        _is_gift: 'true',
        _gift_for: neededMap.get(vid) || '',
      },
    }));
    await fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    });
  }

  async function removeGiftLines(giftLines) {
    const updates = {};
    giftLines.forEach((g) => {
      updates[g.key] = 0;
    });
    await fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates }),
    });
  }

  /**
   * Reorder cart.items so all non-gift (main) products come first and
   * gifts pile up at the end. Returns true if a reorder was performed,
   * false otherwise.
   *
   * Why this exists:
   *   Conventional checkout UX: customer sees the product they're buying
   *   at the top of the order summary, free gifts as bonus items below.
   *   Shopify checkout displays lines in cart-line order, so we have to
   *   physically reorder cart.items, not just style the drawer.
   *
   *   The naive cart order without this reorder looks fine when only one
   *   main product is in the cart ([main A, gift A1, gift A2]), but breaks
   *   when a second main is added. Shopify appends new lines to the end,
   *   so the cart becomes [main A, gift A1, gift A2, main B] — main B
   *   gets buried below A's gifts in checkout, looking broken.
   *
   *   Shopify's AJAX cart API has no position field — the only way to
   *   reorder a line is destructive (remove + re-add, which puts re-added
   *   lines at the end). To get [mains..., gifts...] we remove + re-add
   *   the GIFTS (not the mains) so they end up at the bottom. Re-adding
   *   gifts doesn't pollute analytics: gift add events were ours to fire
   *   in the first place (they came from this script, not the customer).
   *
   *   The reorder ONLY runs when a gift currently appears before a main
   *   in cart.items — i.e. when the order is actually wrong. Steady state
   *   is a no-op.
   *
   * @param {object} cart Current cart object (from /cart.js)
   * @returns {Promise<boolean>}
   */
  async function reorderGiftsToEndIfNeeded(cart) {
    // DISABLED — superseded by the GIFTS-FIRST add interception (see bottom of
    // this file). Gifts-first adds a product's gifts BEFORE the product, so
    // gifts are the OLDER lines and the main is the NEWEST line. This store's
    // checkout renders newest-first, so the main then sits at the TOP of the
    // order summary. Moving gifts to the END here would make them the newest
    // lines again and push the main back to the bottom — the exact opposite of
    // what we want. So this is now a no-op. Orphan removal (toRemove) and gift
    // attachment (toAdd) in sync() are unaffected; only reordering is off.
    return false;
  }

  // eslint-disable-next-line no-unused-vars
  async function _reorderGiftsToEndIfNeeded_DISABLED(cart) {
    let seenGift = false;
    let needsReorder = false;
    for (const item of cart.items) {
      if (isGift(item)) {
        seenGift = true;
      } else if (seenGift) {
        needsReorder = true;
        break;
      }
    }
    if (!needsReorder) return false;

    const giftLines = cart.items.filter(isGift);
    if (giftLines.length === 0) return false;

    await removeGiftLines(giftLines);

    const items = giftLines.map((g) => ({
      id: g.variant_id,
      quantity: 1,
      properties: { ...g.properties },
    }));
    await fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    });
    return true;
  }

  async function sync(seedCart) {
    // Coalescing lock: if a sync is already running, don't drop this
    // request — remember it and re-run once the current pass finishes.
    //
    // Why not the old "if (syncing) return" + 300ms timer: that silently
    // DROPPED any cart:updated that arrived during the lock window. With
    // the refresh removed, syncs are cheap, but a dropped event could
    // still leave gifts orphaned — e.g. customer removes main A (sync
    // starts cleaning A's gifts), then immediately removes main B; B's
    // cart:updated lands mid-sync and would be lost, leaving B's gifts
    // stranded in the cart. Re-running once after settle guarantees the
    // cart always converges to the correct gift set, with no arbitrary
    // timer and no dropped updates.
    if (syncing) {
      rerunRequested = true;
      return;
    }
    syncing = true;

    try {
      // Mark work as pending up-front; relaxed in finally once the cart
      // has settled (and no re-run is queued). The checkout button waits
      // on this so a fast click can't leave before gifts are in the cart.
      AutoGifts.pending = true;

      // Only trust the seed cart on a direct event call. A queued re-run
      // passes no seed, forcing a fresh /cart.js fetch so it sees the
      // latest state (the seed that triggered the re-run is already stale).
      const rules = readRules();
      let cart = seedCart && Array.isArray(seedCart.items) ? seedCart : await fetchCart();

      const needed = computeNeededGifts(cart, rules);
      const present = findGiftLines(cart);

      // Add: gifts in `needed` but not in cart.
      const presentVids = new Set(present.map((g) => Number(g.variant_id)));
      const toAdd = [...needed.keys()].filter((vid) => !presentVids.has(vid));

      // Remove: gifts present that aren't needed, OR duplicate gift lines
      // of the same variant.
      const seenVids = new Set();
      const toRemove = [];
      for (const g of present) {
        const vid = Number(g.variant_id);
        if (!needed.has(vid)) {
          toRemove.push(g);
        } else if (seenVids.has(vid)) {
          toRemove.push(g); // duplicate — keep only one per variant
        } else {
          seenVids.add(vid);
        }
      }

      // Remove first so subsequent adds don't conflict on duplicate detection.
      if (toRemove.length) await removeGiftLines(toRemove);
      if (toAdd.length) await addGifts(needed, toAdd);

      // Keep MAIN products above gifts in cart-line order so checkout shows
      // the product the customer is buying at the top, gifts beneath it.
      // Checkout renders lines oldest-first; when a customer swaps between
      // two products that share gifts, the gift lines can end up older than
      // the newly-added main, pushing the main to the bottom. This corrects
      // that by moving gift lines to the end — but ONLY when the order is
      // actually wrong (see reorderGiftsToEndIfNeeded), so it's a no-op on
      // the common single-product path and adds no round-trips there.
      //
      // Run it on the freshest cart we have: if we just mutated the cart
      // (add/remove), re-fetch so the order check sees the real state;
      // otherwise the seed/fetched `cart` is still accurate.
      let cartForOrder = cart;
      if (toRemove.length || toAdd.length) {
        cartForOrder = await fetchCart();
      }
      await reorderGiftsToEndIfNeeded(cartForOrder);

      // ARKTIC PERF: We do NOT dispatch cart:refresh here, even when we
      // added, removed, or reordered gift lines.
      //
      // The cart drawer renders gift rows from the product metafield
      // (custom.attached_gifts), NOT from the gift lines in cart.items —
      // see sections/cart-drawer.liquid. And the cart summary shows only
      // Fragt + Total (cart.total_price), neither of which depends on gift
      // lines being present (gifts cost 0 via the Shopify Function). So the
      // drawer is already visually correct on the theme's own native render
      // that fired THIS cart:updated. Our job here is only to keep
      // cart.items in sync for CHECKOUT (gifts must actually be in the cart,
      // in the right order, so they appear correctly in the order) — which
      // the add/remove/reorder POSTs above accomplish silently.
      //
      // The reorder only churns GIFT line keys, which the drawer doesn't
      // reference (gift rows come from the metafield and have no remove/qty
      // controls). Main lines are untouched, so remove/qty stay reliable.
      //
      // Previously we dispatched cart:refresh, which made the theme
      // re-fetch the section and re-render the ENTIRE cart body a second
      // time per interaction. With several gift lines present that double
      // render was expensive, and worse: replacing cartDrawerBody.innerHTML
      // mid-interaction destroyed the line the customer had just clicked,
      // which is why the trash icon "did nothing" and +/- felt laggy.
      // Removing the refresh makes each interaction cause exactly ONE
      // render (the theme's own), so the cart stays responsive no matter
      // how many gifts are attached.
    } catch (err) {
      console.error('[auto-gifts] sync failed:', err);
    } finally {
      syncing = false;
      if (rerunRequested) {
        // A cart:updated arrived while we were busy — run once more with a
        // fresh fetch to absorb it. pending stays true across the re-run.
        rerunRequested = false;
        sync();
      } else {
        // Truly settled — release checkout waiters.
        AutoGifts._settle();
      }
    }
  }

  // After every cart change the theme broadcasts, re-evaluate.
  document.addEventListener('cart:updated', (e) => {
    const cart = e && e.detail && e.detail.cart ? e.detail.cart : null;
    sync(cart);
  });

  // Initial sync on page load — catches drift from prior sessions.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => sync());
  } else {
    sync();
  }
})();